import { FileText, Download, CheckCircle2, XCircle } from "lucide-react";
import { Section, Eyebrow } from "@/components/Section";
import { API } from "@/lib/api";

const POTENCY = [
  { name: "Tongkat Ali Extract", claimed: "400 mg", measured: "138 mg", pass: false },
  { name: "Fenugreek Extract", claimed: "500 mg", measured: "486 mg", pass: true },
  { name: "Zinc (as citrate)", claimed: "30 mg", measured: "29 mg", pass: true },
  { name: "Vitamin D3", claimed: "2000 IU", measured: "910 IU", pass: false },
];
const METALS = [
  { name: "Lead (Pb)", val: "< 0.1 ppm" }, { name: "Arsenic (As)", val: "< 0.1 ppm" },
  { name: "Cadmium (Cd)", val: "< 0.05 ppm" }, { name: "Mercury (Hg)", val: "< 0.05 ppm" },
];

export default function SampleReport() {
  return (
    <div data-testid="sample-report-page">
      <section className="bg-ocean">
        <div className="mx-auto max-w-7xl px-6 py-20 text-center md:px-10 md:py-24">
          <Eyebrow>Anonymized example</Eyebrow>
          <h1 className="mx-auto mt-5 max-w-3xl text-4xl font-bold tracking-tight text-white sm:text-5xl">Sample Report</h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-300">This is what you receive — clear measured values against label claims. (Anonymized for demonstration.)</p>
          <a href={`${API}/sample-report`} target="_blank" rel="noreferrer" data-testid="sample-report-download"
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-teal px-7 py-3 text-base font-semibold text-white transition-colors hover:bg-teal-dark">
            <Download className="h-5 w-5" /> Download Sample PDF
          </a>
        </div>
      </section>

      <Section>
        <div className="mx-auto max-w-3xl overflow-hidden rounded-3xl border border-border bg-white shadow-[0_8px_30px_rgba(10,61,98,0.08)]">
          <div className="flex items-center gap-3 border-b border-border bg-ocean px-8 py-6 text-white">
            <FileText className="h-8 w-8 text-teal-light" />
            <div>
              <h2 className="font-heading text-xl font-bold">Certificate of Analysis</h2>
              <p className="text-sm text-slate-300">TestMySupps · Independent Lab Report</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 border-b border-border px-8 py-6 text-sm md:grid-cols-4">
            {[["Sample ID", "VMS-SAMPLE-0001"], ["Tier", "Full Transparency"], ["Method", "HPLC & LC-MS/MS"], ["Status", "Complete"]].map(([k, v]) => (
              <div key={k}><p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{k}</p><p className="mt-1 font-semibold text-ocean">{v}</p></div>
            ))}
          </div>
          <div className="px-8 py-7">
            <h3 className="text-lg font-semibold text-ocean">Potency Results</h3>
            <div className="mt-4 overflow-hidden rounded-xl border border-border">
              <table className="w-full text-sm">
                <thead className="bg-secondary text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <tr><th className="px-4 py-3">Analyte</th><th className="px-4 py-3">Claimed</th><th className="px-4 py-3">Measured</th><th className="px-4 py-3">Result</th></tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {POTENCY.map((r) => (
                    <tr key={r.name}>
                      <td className="px-4 py-3 font-medium text-slate-700">{r.name}</td>
                      <td className="px-4 py-3 text-slate-600">{r.claimed}</td>
                      <td className="px-4 py-3 text-slate-600">{r.measured}</td>
                      <td className="px-4 py-3">
                        {r.pass
                          ? <span className="inline-flex items-center gap-1 font-semibold text-emerald-600"><CheckCircle2 className="h-4 w-4" /> Pass</span>
                          : <span className="inline-flex items-center gap-1 font-semibold text-red-500"><XCircle className="h-4 w-4" /> Fail</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3 className="mt-8 text-lg font-semibold text-ocean">Heavy Metals Screen</h3>
            <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
              {METALS.map((m) => (
                <div key={m.name} className="rounded-xl border border-border bg-background p-4">
                  <p className="text-sm font-semibold text-ocean">{m.name}</p>
                  <p className="mt-1 text-sm text-slate-600">{m.val}</p>
                  <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-emerald-600"><CheckCircle2 className="h-3.5 w-3.5" /> Pass</span>
                </div>
              ))}
            </div>
            <p className="mt-8 border-t border-border pt-5 text-xs leading-relaxed text-slate-400">
              We report measured values only and make no health claims. We are a testing service only and do not provide medical advice.
            </p>
          </div>
        </div>
      </Section>
    </div>
  );
}
